/**
 * 週期結果回饋：對齊 interval notify → L → H 策略。
 * 準不準已退役；此模組只處理「提醒後未預約／提早叫／過區間才叫」。
 */

import { daysUntil, getTodayIso } from "./intervalGasView.model";

export type CycleOutcomeKind =
  | "missed_soft"
  | "missed_in_window"
  | "missed_past_h"
  | "early_order"
  | "late_order";

export type CycleOutcomeTone = "warn" | "danger" | "info";

export interface CycleOutcomeInput {
  notifyStandardDate?: string | null;
  dispatchLowerDate?: string | null;
  observeUpperDate?: string | null;
  /** DEV／測試強制指定；優先於日期推導 */
  forceKind?: CycleOutcomeKind | "none" | null;
  /**
   * 過 H 後仍顯示的寬限天數（避免永久打擾）。
   * 超過則回 null。
   */
  pastHGraceDays?: number;
}

export interface CycleOutcomeView {
  kind: CycleOutcomeKind;
  tone: CycleOutcomeTone;
  badge: string;
  title: string;
  body: string;
  showOrderCta: boolean;
  /** 未預約類才顯示「暫停本次通知」 */
  showPauseNotify: boolean;
  chips: { key: string; label: string }[];
  inputPlaceholder: string;
}

/** 提醒日：較輕，仍可回報 */
const SOFT_CHIPS = [
  { key: "will_soon", label: "這幾天會預約" },
  { key: "still_ok", label: "目前氣還夠" },
  { key: "already_ordered", label: "已用其他方式叫過" },
  { key: "away", label: "人不在／出國" },
];

/** 區間內未預約：強調出國／外出等特殊狀況 */
const WINDOW_CHIPS = [
  { key: "abroad", label: "出國／外出中" },
  { key: "still_ok", label: "目前氣還夠" },
  { key: "will_soon", label: "這幾天會預約" },
  { key: "already_ordered", label: "已用其他方式叫過" },
];

const PAST_H_CHIPS = [
  { key: "abroad", label: "出國／外出中" },
  { key: "still_ok", label: "目前氣還夠" },
  { key: "already_ordered", label: "已用其他方式叫過" },
  { key: "will_soon", label: "這幾天會預約" },
];

const EARLY_CHIPS = [
  { key: "running_low", label: "用得比較快" },
  { key: "guest", label: "用量臨時變多" },
  { key: "habit", label: "習慣提早預約" },
  { key: "other", label: "其他原因" },
];

const LATE_CHIPS = [
  { key: "missed_remind", label: "沒注意到提醒" },
  { key: "abroad", label: "出國／外出中" },
  { key: "still_had_gas", label: "當時氣還夠" },
  { key: "other", label: "其他原因" },
];

/** 暫停本輪通知的原因 */
export const PAUSE_NOTIFY_CHIPS: { key: string; label: string }[] = [
  { key: "abroad_1w", label: "出國約 1 週" },
  { key: "abroad_2w", label: "出國約 2 週" },
  { key: "away", label: "人不在家" },
  { key: "still_ok", label: "氣還夠，暫時不需" },
  { key: "other", label: "其他原因" },
];

const VIEW: Record<CycleOutcomeKind, Omit<CycleOutcomeView, "kind">> = {
  missed_soft: {
    tone: "warn",
    badge: "30秒問答",
    title: "叫桶提醒日已到",
    body: "方便告訴我們目前狀況嗎？",
    showOrderCta: false,
    showPauseNotify: true,
    chips: SOFT_CHIPS,
    inputPlaceholder: "例如：下週才需要、瓦斯行已來電…",
  },
  missed_in_window: {
    tone: "danger",
    badge: "30秒問答",
    title: "已進入預估用完區間",
    body: "還沒預約，有其他狀況嗎？",
    showOrderCta: false,
    showPauseNotify: true,
    chips: WINDOW_CHIPS,
    inputPlaceholder: "例如：出國到月底、家裡還有備瓶…",
  },
  missed_past_h: {
    tone: "danger",
    badge: "30秒問答",
    title: "已超過預估用完日",
    body: "還沒預約，方便告訴我們原因嗎？",
    showOrderCta: false,
    showPauseNotify: true,
    chips: PAST_H_CHIPS,
    inputPlaceholder: "例如：已自行叫桶、出國剛回來…",
  },
  early_order: {
    tone: "info",
    badge: "30秒問答",
    title: "這次預約得比較早",
    body: "有特殊狀況嗎？",
    showOrderCta: false,
    showPauseNotify: false,
    chips: EARLY_CHIPS,
    inputPlaceholder: "例如：客人來、煮年菜…",
  },
  late_order: {
    tone: "warn",
    badge: "30秒問答",
    title: "這次比較晚才預約",
    body: "有特殊狀況嗎？",
    showOrderCta: false,
    showPauseNotify: false,
    chips: LATE_CHIPS,
    inputPlaceholder: "例如：出差、忘了看通知…",
  },
};

export function buildCycleOutcomeView(kind: CycleOutcomeKind): CycleOutcomeView {
  return { kind, ...VIEW[kind] };
}

/** 底部浮層：過區間仍未預約、較晚預約回饋；內容區：提醒日／區間內／提早 */
export function isUrgentCycleOutcome(kind: CycleOutcomeKind): boolean {
  return kind === "missed_past_h" || kind === "late_order";
}

/**
 * 依計劃日推導「提醒後未預約」結果；early/late_order 需 forceKind（下單事件後端尚未接）。
 */
export function resolveCycleOutcomeKind(input: CycleOutcomeInput): CycleOutcomeKind | null {
  if (input.forceKind === "none") return null;
  if (input.forceKind) return input.forceKind;

  const notify = input.notifyStandardDate;
  const L = input.dispatchLowerDate;
  const H = input.observeUpperDate;
  if (!notify || !L || !H) return null;

  const dNotify = daysUntil(notify);
  const dL = daysUntil(L);
  const dH = daysUntil(H);
  if (dNotify === null || dL === null || dH === null) return null;

  // 尚未到提醒日 → 安靜
  if (dNotify > 0) return null;

  const grace = input.pastHGraceDays ?? 14;
  if (dH < -grace) return null;

  if (dH < 0) return "missed_past_h";
  if (dL <= 0) return "missed_in_window";
  return "missed_soft";
}

export function cycleOutcomeAckKey(
  contextId: string | number | null | undefined,
  kind: CycleOutcomeKind,
  anchorDate: string,
): string {
  return `cycle_outcome_ack_v1:${contextId ?? "default"}:${kind}:${anchorDate}`;
}

export function loadCycleOutcomeAck(
  contextId: string | number | null | undefined,
  kind: CycleOutcomeKind,
  anchorDate: string,
): { chip: string; note?: string } | null {
  try {
    const raw = localStorage.getItem(cycleOutcomeAckKey(contextId, kind, anchorDate));
    if (!raw) return null;
    const p = JSON.parse(raw) as { date: string; chip: string; note?: string; sub?: string };
    return p.date === getTodayIso() ? { chip: p.chip, note: p.note ?? p.sub } : null;
  } catch {
    return null;
  }
}

export function saveCycleOutcomeAck(
  contextId: string | number | null | undefined,
  kind: CycleOutcomeKind,
  anchorDate: string,
  chip: string,
  note?: string,
): void {
  try {
    localStorage.setItem(
      cycleOutcomeAckKey(contextId, kind, anchorDate),
      JSON.stringify({ date: getTodayIso(), chip, note: note?.trim() || undefined }),
    );
  } catch {
    /* silent */
  }
}

export function clearCycleOutcomeAck(
  contextId: string | number | null | undefined,
  kind: CycleOutcomeKind,
  anchorDate: string,
): void {
  try {
    localStorage.removeItem(cycleOutcomeAckKey(contextId, kind, anchorDate));
  } catch {
    /* silent */
  }
}

/** 暫停本輪預測通知（綁 last_order／週期錨點，新叫桶後自然解除） */
export function cyclePauseKey(
  contextId: string | number | null | undefined,
  cycleAnchor: string,
): string {
  return `cycle_notify_pause_v1:${contextId ?? "default"}:${cycleAnchor}`;
}

export function loadCycleNotifyPause(
  contextId: string | number | null | undefined,
  cycleAnchor: string,
): { reason: string; note?: string } | null {
  try {
    const raw = localStorage.getItem(cyclePauseKey(contextId, cycleAnchor));
    if (!raw) return null;
    return JSON.parse(raw) as { reason: string; note?: string };
  } catch {
    return null;
  }
}

export function saveCycleNotifyPause(
  contextId: string | number | null | undefined,
  cycleAnchor: string,
  reason: string,
  note?: string,
): void {
  try {
    localStorage.setItem(
      cyclePauseKey(contextId, cycleAnchor),
      JSON.stringify({ reason, note: note?.trim() || undefined, at: getTodayIso() }),
    );
  } catch {
    /* silent */
  }
}

export function clearCycleNotifyPause(
  contextId: string | number | null | undefined,
  cycleAnchor: string,
): void {
  try {
    localStorage.removeItem(cyclePauseKey(contextId, cycleAnchor));
  } catch {
    /* silent */
  }
}

/** DEV 面板用的全部結果狀態 */
export const DEV_OUTCOME_OPTIONS: {
  key: CycleOutcomeKind | "auto" | "none";
  label: string;
  desc: string;
}[] = [
  { key: "auto", label: "自動（依日期）", desc: "用 notify／L／H 推導" },
  { key: "none", label: "安靜（不顯示）", desc: "強制隱藏結果卡" },
  { key: "missed_soft", label: "一般・提醒日", desc: "內容區 · notify ≤ today < L" },
  { key: "missed_in_window", label: "一般・區間內", desc: "內容區 · L ≤ today ≤ H" },
  { key: "early_order", label: "一般・提早預約", desc: "內容區 · 下單 < notify" },
  { key: "missed_past_h", label: "浮層・過區間未預約", desc: "底部浮層 · today > H" },
  { key: "late_order", label: "浮層・較晚預約", desc: "底部浮層 · 下單 > H" },
];
